import styled from '@emotion/styled';
import { Constants, Theme } from '@seedlang/constants';
import { WordTranslateCardWrapper } from '@seedlang/hoc';
import { DeckUI, OverlayUI } from '@seedlang/state';
import { isBlank, isPresent } from '@seedlang/utils';
import CardArrowBox from 'components/card/card_arrow_box';
import CardUserInput from 'components/card/card_user_input';
import HintWarning from 'components/card/hint_warning';
import CardContent from 'components/layout/card_content';
import CardWrapper from 'components/layout/card_wrapper';
import Answer from 'components/vocab_card/answer';
import CardSection from 'components/vocab_card/card_section';
import CardSourceList from 'components/vocab_card/card_source_list';
import ExpandedTarget from 'components/vocab_card/expanded_target';
import { ExampleSentenceToggle } from 'components/vocab_card/example_sentence';
import MediaSection from 'components/vocab_card/media_section';
import TranslateButtons from 'components/vocab_card/translate_buttons';
import WordEntry from 'components/word_entry';
import { observer } from 'mobx-react';
import React from 'react';

const Hint = styled.span`
  font-size: 18px;
  text-align: center;
`;

const HintPart = styled.span`
  vertical-align: baseline;
  user-select: none;
`;

const HintSentence = styled.div`
  border: 0;
  padding: 0;
  width: 100%;
  min-width: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-weight: normal;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: ${Theme.z['card-3']};
  .fa-times {
    color: ${Theme.red};
  }
  .fa-check {
    color: ${Theme.green};
  }
`;

const HintWrapper = styled.div`
  position: ${props => props.floating ? 'absolute' : 'relative'};
  top: ${props => props.floating ? `calc(50% + ${props.inputHeight / 2}px)` : 'auto'};
  max-height: ${props => props.floating ? `calc(50% - ${props.inputHeight / 2}px - 10px)` : 'none'};
  overflow-y: auto;
  flex-shrink: 0;
  width: calc(100% - 40px);
`;

const WordEntryWrapper = styled.div`
  width: 100%;
  flex-shrink: 0;
`;

const TargetSection = styled(CardSection)`
  overflow-y: ${props => props.scrollHints ? 'auto' : 'hidden'};
`;

const SourceText = styled.span`
  cursor: pointer;
`;

@observer
class WordTranslateCard extends React.Component {
  state = { inputHeight: 0 };

  measureInput = element => {
    this.inputObserver?.disconnect();
    if (!element) return;
    this.inputObserver = new ResizeObserver(() => {
      const inputHeight = Math.ceil(element.getBoundingClientRect().height);
      this.setState(previous => previous.inputHeight === inputHeight ? null : { inputHeight });
    });
    this.inputObserver.observe(element);
  };

  componentWillUnmount() {
    this.inputObserver?.disconnect();
  }

  onClickTargetSection = () => {
    DeckUI.openDeckVocabCard({
      card: this.props.card,
      cardState: this.props.cardState,
      requiresRevealed: true,
    });
  }

  openSourceEdit = () => {
    DeckUI.createEvent('deck - click translations icon', {
      language_id: this.props.card?.languageId || DeckUI.deck?.languageId,
      card_type_id: this.props.card?.cardType?.id || this.props.card?.cardTypeId,
    });
    OverlayUI.addOverlay('sourceEdit');
  }

  showHint = event => {
    event?.stopPropagation?.();
    if (!this.props.inView) return;
    this.props.setShowHelp(true);
    this.props.onHelpButtonClicked();
  }

  render() {
    const showHintOrWarning = !this.props.cardState.revealed && (isPresent(this.props.hint) || isPresent(this.props.sentenceHint) || isPresent(this.props.warning));
    const floatingHints = this.props.shouldFloatHints(this.state.inputHeight, 36);
    const scrollHints = showHintOrWarning && !this.props.showUserInput && !floatingHints;
    return (
      <CardWrapper
        card={this.props.card}
        cardState={this.props.cardState}
        userCard={this.props.userCard}
        icons={this.props.icons}
      >
        <CardContent>
          {
            this.props.cardState.revealed && this.props.card.hasTargetVideoClip &&
              <MediaSection
                showMedia
                autoPlay={this.props.inView}
                card={this.props.card}
                cardState={this.props.cardState}
                borderTopRadius={10}
                height={this.props.sectionHeight}
                afterVideoPlayback={this.props.afterVideoPlayback}
              />
          }
          <CardSection
            background='#FFF'
            height={this.props.sectionHeight}
            numSections={this.props.numSections}
            borderTopRadius={this.props.cardState.revealed && this.props.card.hasTargetVideoClip ? 0 : 10}
            fontSize={(this.props.card.word.isNumber && this.props.card.word.digitSourceText) ? '34px' : null}
          >
            {
              (!this.props.card.word.isNumber || !this.props.card.word.digitSourceText) &&
                <CardSourceList
                  center
                  fontSize={DeckUI.layout.isMobile ? '20px' : '26px'}
                  background='#FFF'
                  showEdit
                  word={this.props.card.word}
                  card={this.props.card}
                />
            }
            {
              this.props.card.word.isNumber && this.props.card.word.digitSourceText &&
                <SourceText
                  onClick={this.openSourceEdit}
                >
                  {this.props.card.word.formattedDigitSourceText}
                </SourceText>
            }
          </CardSection>
          <TargetSection
            background='#CCC'
            scrollHints={scrollHints}
            justifyContent={scrollHints ? 'flex-start' : 'center'}
            height={this.props.sectionHeight}
            numSections={this.props.numSections}
            borderBottomRadius={this.props.showUserInput ? 0 : 10}
            onClick={this.props.showExpandedTarget && DeckUI.canOpenDeckVocabCard(this.props.card, this.props.cardState, true) ? this.onClickTargetSection : null}
          >
            {!this.props.cardState.revealed && this.props.showHelpButton &&
              <ExampleSentenceToggle showHint onShowHint={this.showHint} disabled={!this.props.inView} />
            }

            {
              !this.props.showUserInput && this.props.cardState.revealed &&
                <IconWrapper>
                  {
                    this.props.userCard.correct &&
                      <i className='fa fa-check' />
                  }
                  {
                    !this.props.userCard.correct && isBlank(this.props.cardState.textEntry) &&
                      <i className='fa fa-times' />
                  }
                </IconWrapper>
            }
            {
              !this.props.showUserInput && !this.props.showExpandedTarget && this.props.card.word &&
                <WordEntryWrapper ref={this.measureInput}>
                  <WordEntry
                    disabled={!this.props.inView}
                    focus={this.props.inView && DeckUI.firstCardHasAdvanced}
                    target={this.props.card.word.targetTextWithDefiniteArticle}
                    message={this.props.message}
                    onChange={this.props.onTextInputChange}
                    onPressEnter={() => DeckUI.onEvaluateTextInput(this.props.card, this.props.cardState, this.props.userCard)}
                    value={this.props.cardState.textEntry}
                    hint={this.props.hint}
                    languageId={this.props.card.languageId || DeckUI.deck.languageId || 'DE'}
                    incorrect={this.props.cardState.revealed && this.props.vocabCardIncorrect}
                    revealed={this.props.cardState.revealed}
                    revealedValue={this.props.wrongAnswerWithDiff}
                  />
                </WordEntryWrapper>
            }
            {
              this.props.showExpandedTarget &&
                <ExpandedTarget
                  card={this.props.card}
                  cardState={this.props.cardState}
                  showWrongAnswer={this.props.vocabCardIncorrect}
                  wrongAnswer={this.props.cardState.textEntry}
                  height={DeckUI.vocabTextHeight}
                  word={this.props.card.word}
                />
            }
            {
              this.props.cardState.revealed && !this.props.showExpandedTarget && isPresent(this.props.card.word) &&
                <Answer
                  correct
                  text={this.props.correctAnswerWithDiff}
                  marginBottom='10px'
                />
            }
            {
              showHintOrWarning &&
              <HintWrapper floating={floatingHints} inputHeight={this.state.inputHeight}>
                {isPresent(this.props.warning) && isPresent(Constants.WARNINGS[this.props.warning]) &&
                  <HintWarning padding="8px 10px" style={{ minHeight: 0, marginBottom: 8 }}>
                    {Constants.WARNINGS[this.props.warning]}
                  </HintWarning>
                }
                <HintWarning
                  background="transparent"
                  color="#333"
                  padding="8px 10px"
                  style={{ minHeight: 0 }}
                >
                  { this.props.sentenceHint &&
                    <HintSentence>
                      {this.props.hintParts.map((part, index) => <HintPart key={index} aria-label={part.masked ? "hidden letters" : undefined}>{part.masked ? '_'.repeat(Array.from(part.text).length) : part.text}</HintPart>)}
                    </HintSentence>
                  }
                  { !this.props.sentenceHint && this.props.hint && <Hint>{this.props.hintParts.map((part, index) => <HintPart key={index} aria-label={part.masked ? "hidden letters" : undefined}>{part.masked ? '_'.repeat(Array.from(part.text).length) : part.text}</HintPart>)}</Hint> }
                </HintWarning>
              </HintWrapper>
            }
          </TargetSection>
          {
            this.props.showUserInput &&
              <CardUserInput
                card={this.props.card}
                cardState={this.props.cardState}
                userCard={this.props.userCard}
                disabled={!this.props.inView}
              />
          }
          <CardArrowBox
            card={this.props.card}
            possibleArrowBoxes={this.props.possibleArrowBoxes}
            currentArrowBox={this.props.currentArrowBox}
          />
        </CardContent>
        <TranslateButtons
          onNextClick={this.props.hideOnboardingPopups}
          advanceCard
          card={this.props.card}
          cardState={this.props.cardState}
          userCard={this.props.userCard}
          disabled={!this.props.inView}
        />
      </CardWrapper>
    );
  }
}

export default WordTranslateCardWrapper(WordTranslateCard);
